import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { Alert, Button, LoadingBlock } from '../components/ui.tsx';
import './schedule-pages.css';
import { colorMatches, type VisitColors } from '@shared/schedule-colors.ts';
import { ColorLegend, VisitColorSignals, useColorCopy } from '../components/schedule/visit-colors.tsx';
import { lazy, Suspense, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { localDateTimeToUtc } from '@shared/calculations/recurrence.ts';
import { api } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { todayInTimezone } from '../components/schedule/format.ts';
import type { MapPin } from '../components/live-map/street-map.tsx';
import { formatInstantDateTime, formatInstantTime } from '../i18n/datetime.ts';
import { usableMapPoint, type MapLocation } from '@shared/map-viewport.ts';
const StreetMap = lazy(() => import('../components/live-map/street-map.tsx'));
export function validMapDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(value + 'T00:00:00Z');
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === value;
}
interface Visit {
  colors?: VisitColors;
  id: string;
  label: string;
  version: number;
  status: string;
  startsAt: string;
  endsAt: string;
  assigneeIds: string[];
  property: { lat: number; lng: number } | null;
}
interface MapDay {
  routeTeams?: Array<{ id: string; name: string }>;
  visits: Visit[];
  crews: {
    userId: string;
    name: string;
    position?: { lat: number; lng: number; recordedAt: string; accuracyM: number } | null;
  }[];
  openSlots: {
    id: string;
    appointmentId: string;
    startsAt: string;
    version: number;
    property?: { lat: number; lng: number } | null;
  }[];
  entitlements: { drag: boolean; routeOptimization?: boolean };
}
/** A discrete planned stop, never an invented live location or interpolated GPS trail. */
export function plannedPins(visits: Visit[], crews: MapDay['crews'], instant: number): MapPin[] {
  return crews.flatMap((crew) => {
    const itinerary = visits
      .filter((v) => v.assigneeIds.includes(crew.userId) && v.property)
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    const stop = itinerary.filter((v) => Date.parse(v.startsAt) <= instant).at(-1);
    return stop?.property
      ? [{ ...stop.property, id: crew.userId, label: `${crew.name} · Planned · ${stop.label}`, planned: true }]
      : [];
  });
}
export function prefersMap(params: URLSearchParams): boolean {
  if (['appointment', 'complete', 'location', 'paymentStatus'].some((key) => params.has(key))) return false;
  return params.get('view') === 'map';
}
export function LiveMapLink({ customers = false }: { customers?: boolean }) {
  const { company, user, currentLocation } = useSession();
  const { i18n } = useTranslation();
  const capabilities = useQuery({
    queryKey: ['live-map-capabilities', company?.id, user?.id, currentLocation?.id],
    queryFn: ({ signal }) => api.getFresh<{ enabled: boolean; dayMap: boolean }>('/schedule/map-capabilities', signal),
    refetchInterval: 15000,
    retry: false,
  });
  return capabilities.data?.enabled && !capabilities.error && (customers || capabilities.data.dayMap) ? (
    <Link
      className="font-semibold text-[color:var(--color-brand)] underline"
      to={customers ? '/customers/map' : '/schedule?view=map'}
    >
      {i18n.language.startsWith('es')
        ? customers
          ? 'Mapa de clientes'
          : 'Mapa del día'
        : customers
          ? 'Customer map'
          : 'Day map'}
    </Link>
  ) : null;
}
export default function LiveMapPage({
  customers = false,
  scheduleSelector,
}: {
  customers?: boolean;
  scheduleSelector?: (disabled: boolean) => ReactNode;
}) {
  const { company, user, currentLocation } = useSession();
  const { i18n } = useTranslation();
  const es = i18n.language.startsWith('es');
  const words = (en: string, spanish: string) => (es ? spanish : en);
  const timezone = currentLocation?.timezone ?? company?.timezone ?? 'UTC';
  const [params] = useSearchParams();
  const [crewChoice, setCrewChoice] = useState<Record<string, string>>({});
  const routeDate = params.get('date');
  const dateScope = JSON.stringify([company?.id, user?.id, currentLocation?.id, timezone, routeDate]);
  const initialDate = validMapDate(routeDate) ? routeDate : todayInTimezone(timezone);
  const [dateChoice, setDateChoice] = useState(() => ({ scope: dateScope, value: initialDate }));
  // Reset synchronously so a new URL or location never fetches the previous
  // scope's manually selected day, even for the first render after navigation.
  if (dateChoice.scope !== dateScope) setDateChoice({ scope: dateScope, value: initialDate });
  const date = dateChoice.scope === dateScope ? dateChoice.value : initialDate;
  const setDate = (value: string) => setDateChoice({ scope: dateScope, value });
  const [minutes, setMinutes] = useState(9 * 60),
    [positionMode, setPositionMode] = useState<'punch' | 'planned'>('punch'),
    [selected, setSelected] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const scope = [company?.id, user?.id, currentLocation?.id];
  const capability = useQuery({
    queryKey: ['live-map-capabilities', ...scope],
    queryFn: ({ signal }) =>
      api.getFresh<{ enabled: boolean; dayMap: boolean; styleUrl: string | null }>(
        '/schedule/map-capabilities',
        signal,
      ),
    refetchInterval: 15000,
    retry: false,
  });
  const enabled = capability.data?.enabled && !capability.error && (customers || capability.data.dayMap);
  const data = useQuery({
    queryKey: ['live-map-day', ...scope, date, customers],
    queryFn: ({ signal }) =>
      api.getFresh<
        MapDay & {
          properties: (MapPin & {
            customerId?: string;
            customerName?: string;
            propertyName?: string;
            address?: string;
          })[];
          truncated?: boolean;
        }
      >(customers ? '/customers/map' : `/schedule/day-map?date=${date}`, signal),
    enabled: !!enabled,
    refetchInterval: 15000,
    retry: false,
  });
  const [colorFilter, setColorFilter] = useState('');
  const colorWords = useColorCopy();
  const visits = (data.data?.visits ?? []).filter((v) => colorMatches(v.colors, colorFilter)),
    crews = data.data?.crews ?? [];
  const pins = useMemo(() => {
    if (customers) return (data.data?.properties ?? []).filter(usableMapPoint);
    const instant = Date.parse(
      localDateTimeToUtc(
        date,
        `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
        timezone,
      ),
    );
    return [
      ...visits
        .filter((v) => v.property)
        .map((v) => ({
          ...v.property!,
          id: v.id,
          label: [
            v.label,
            ...[v.colors?.primary, ...(v.colors?.indicators ?? [])].flatMap((s) =>
              s ? [s.label || colorWords.unknown] : [],
            ),
          ].join(' · '),
          colors: v.colors,
          status: v.assigneeIds.length ? v.status : 'unassigned',
          draggable: !busy && data.data?.entitlements?.drag && ['scheduled', 'confirmed'].includes(v.status),
        })),
      ...(data.data?.openSlots ?? [])
        .filter((slot) => slot.property)
        .map((slot) => ({
          ...slot.property!,
          id: slot.id,
          label: words('Open slot', 'Horario disponible'),
          status: 'cancelled',
          opening: true,
        })),
      ...(positionMode === 'planned'
        ? plannedPins(visits, crews, instant)
        : crews
            .filter((crew) => crew.position && crew.position.accuracyM <= 150)
            .map((crew) => ({
              id: crew.userId,
              lat: crew.position!.lat,
              lng: crew.position!.lng,
              label: `${crew.name} · ${words('Last clock punch', 'Último registro')}: ${formatInstantDateTime(crew.position!.recordedAt, timezone)}`,
              status: Date.now() - Date.parse(crew.position!.recordedAt) > 600000 ? 'unassigned' : 'confirmed',
              planned: true,
            }))),
    ];
  }, [customers, data.data, minutes, date, timezone, busy, positionMode, colorFilter, colorWords]);
  const viewportPoints = customers
    ? (data.data?.properties ?? []).filter(usableMapPoint)
    : visits.flatMap((visit) => (usableMapPoint(visit.property) ? [visit.property] : []));
  const mapLocation = useQuery({
    queryKey: ['live-map-location', ...scope, customers],
    queryFn: ({ signal }) =>
      api.getFresh<MapLocation>(
        `/schedule/map-location?locationId=${encodeURIComponent(currentLocation!.id)}${customers ? '&customers=true' : ''}`,
        signal,
      ),
    enabled: !!enabled && !!currentLocation?.id && data.isSuccess && !data.error && !viewportPoints.length,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const fallbackCenter = mapLocation.data?.locationId === currentLocation?.id ? mapLocation.data?.center : null;
  const mutate = async (path: string, body: object, patch = false) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await (patch ? api.patch : api.post)(path, { ...body, clientMutationId: crypto.randomUUID() });
      await data.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save');
      await data.refetch();
    } finally {
      setBusy(false);
    }
  };
  const title = words(customers ? 'Customer map' : 'Day map', customers ? 'Mapa de clientes' : 'Mapa del día');
  // Properties with no coordinates draw no pin; say so rather than leave a
  // blank map looking like nobody has an address.
  const unplaced = customers ? (data.data?.properties ?? []).length - pins.length : 0;
  if (!enabled)
    return (
      <WorkPage title={title}>
        {capability.isLoading ? (
          <LoadingBlock label={words('Loading map…', 'Cargando mapa…')} />
        ) : capability.error ? (
          <Alert tone="error" title={words('Map could not load', 'No se pudo cargar el mapa')}>
            <p>{words('Refresh failed. Map data is unavailable.', 'No se pudo actualizar el mapa.')}</p>
            <Button className="mt-3" busy={capability.isFetching} onClick={() => void capability.refetch()}>
              {words('Retry', 'Reintentar')}
            </Button>
          </Alert>
        ) : (
          <WorkEmpty
            title={words('The map is unavailable for this account.', 'El mapa no está disponible para esta cuenta.')}
          >
            <Link className="btn btn-secondary mt-3" to={customers ? '/customers' : '/schedule?view=list'}>
              {words(customers ? 'View customers' : 'View schedule', customers ? 'Ver clientes' : 'Ver agenda')}
            </Link>
          </WorkEmpty>
        )}
      </WorkPage>
    );
  return (
    <WorkPage
      title={title}
      description={words(
        customers
          ? 'Customer addresses and location details.'
          : 'Visits, crew positions and openings for the selected day.',
        customers
          ? 'Direcciones de clientes y detalles de ubicación.'
          : 'Visitas, ubicaciones del equipo y horarios disponibles para el día seleccionado.',
      )}
    >
      <div className="map-page space-y-4">
        {!customers && (
          // Getting through the day is Today's route: booked order, directions
          // from wherever you are. This map is for seeing and rearranging it.
          <div className="card flex flex-wrap items-center justify-between gap-3">
            <p>
              {words(
                'Driving today’s visits? Today’s route lists them in booked order with one-tap directions.',
                '¿Va a hacer las visitas de hoy? La ruta de hoy las muestra en el orden reservado, con indicaciones en un toque.',
              )}
            </p>
            <Link className="btn btn-primary" to="/today">
              {words('Today’s route', 'Ruta de hoy')}
            </Link>
          </div>
        )}
        {!customers && (
          <WorkSection title={words('Map controls', 'Controles del mapa')}>
            <div className="map-controls">
              {!customers && (
                <>
                  <label>
                    {words('Crew position', 'Ubicación del equipo')}{' '}
                    <select
                      className="field-control"
                      value={positionMode}
                      onChange={(event) => setPositionMode(event.target.value as 'punch' | 'planned')}
                    >
                      <option value="punch">{words('Last clock punch', 'Último registro')}</option>
                      <option value="planned">{words('Planned stops', 'Paradas planificadas')}</option>
                    </select>
                  </label>
                  {scheduleSelector ? (
                    scheduleSelector(false)
                  ) : (
                    <Link className="font-semibold text-[color:var(--color-brand)] underline" to="/schedule?view=list">
                      {words('Day list / Week / Month', 'Lista / Semana / Mes')}
                    </Link>
                  )}
                </>
              )}
              {!customers && (
                <>
                  <label>
                    {words('Day', 'Día')}{' '}
                    <input
                      aria-label="Day"
                      className="field-control"
                      type="date"
                      value={date}
                      onChange={(e) => {
                        if (e.target.value) setDate(e.target.value);
                      }}
                    />
                  </label>
                  <label>
                    {words('Planned time', 'Hora planificada')} {String(Math.floor(minutes / 60)).padStart(2, '0')}:
                    {String(minutes % 60).padStart(2, '0')}{' '}
                    <input
                      aria-label="Planned time"
                      type="range"
                      min={360}
                      max={1260}
                      step={15}
                      value={minutes}
                      onChange={(e) => {
                        setMinutes(Number(e.target.value));
                        setPositionMode('planned');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === '[' || e.key === ']') {
                          setPositionMode('planned');
                          e.preventDefault();
                          setMinutes((v) => Math.max(360, Math.min(1260, v + (e.key === ']' ? 15 : -15))));
                        }
                      }}
                    />
                  </label>
                  <p>
                    {words(
                      'The scrubber shows planned stops. Clock punches show their recorded time and turn grey after ten minutes.',
                      'La barra muestra paradas planificadas. Los registros muestran su hora y se vuelven grises después de diez minutos.',
                    )}
                  </p>
                </>
              )}
              {!customers && (
                <ColorLegend items={data.data?.visits ?? []} value={colorFilter} onChange={setColorFilter} />
              )}
            </div>
          </WorkSection>
        )}
        {error && <Alert tone="error">{error}</Alert>}
        {data.error && (
          <Alert tone="error">
            <p>{words('Refresh failed. Map data is unavailable.', 'No se pudo actualizar el mapa.')}</p>
            <Button className="mt-3" busy={data.isFetching} onClick={() => void data.refetch()}>
              {words('Retry', 'Reintentar')}
            </Button>
          </Alert>
        )}
        {data.isLoading && <LoadingBlock label={words('Loading map data…', 'Cargando datos del mapa…')} />}
        {customers && !data.error && unplaced > 0 && (
          <Alert tone="warning">
            <p role="status">
              {data.data?.truncated
                ? words(
                    `Of the first 500 properties shown, we couldn't place ${unplaced} on the map. Check their addresses in the list below.`,
                    `De las primeras 500 propiedades mostradas, no pudimos ubicar ${unplaced} en el mapa. Revise sus direcciones en la lista de abajo.`,
                  )
                : unplaced === 1
                  ? words(
                      "We couldn't place 1 property on the map. Check its address in the list below.",
                      'No pudimos ubicar 1 propiedad en el mapa. Revise su dirección en la lista de abajo.',
                    )
                  : words(
                      `We couldn't place ${unplaced} properties on the map. Check their addresses in the list below.`,
                      `No pudimos ubicar ${unplaced} propiedades en el mapa. Revise sus direcciones en la lista de abajo.`,
                    )}
            </p>
          </Alert>
        )}
        {!data.error && capability.data?.styleUrl ? (
          <Suspense fallback={<LoadingBlock label={words('Loading map…', 'Cargando mapa…')} />}>
            <StreetMap
              style={capability.data.styleUrl}
              scopeKey={JSON.stringify([...scope, date, customers, colorFilter])}
              pins={pins}
              viewportPoints={viewportPoints}
              fallbackCenter={data.isPending ? null : fallbackCenter}
              loading={data.isPending || mapLocation.isFetching}
              locationError={mapLocation.isError}
              onSelect={setSelected}
              onReassign={(id) => document.getElementById(`map-crew-${id}`)?.focus()}
            />
          </Suspense>
        ) : (
          <p>
            {words(
              'Street map is not configured. Your visits are listed below.',
              'El mapa de calles no está configurado. Las visitas aparecen abajo.',
            )}
          </p>
        )}
        {customers && !data.error && (
          <WorkSection title={words('Customer addresses', 'Direcciones de clientes')}>
            {data.data?.truncated && (
              <p role="status">
                {words(
                  'Showing the first 500 properties. More properties are available in Customers.',
                  'Se muestran las primeras 500 propiedades. Hay más propiedades en Clientes.',
                )}{' '}
                <Link className="font-semibold text-[color:var(--color-brand)] underline" to="/customers">
                  {words('View customers', 'Ver clientes')}
                </Link>
              </p>
            )}
            {capability.data?.dayMap === false && (
              <p>
                {words(
                  'See your whole day on the map. Starter puts every visit and crew here.',
                  'Vea todo su día en el mapa. Starter muestra cada visita y cada equipo aquí.',
                )}{' '}
                <Link
                  className="font-semibold text-[color:var(--color-brand)] underline"
                  to="/settings#settings-billing"
                >
                  {words('Try Starter', 'Pruebe Starter')}
                </Link>
              </p>
            )}
            <ul className="map-rows">
              {(data.data?.properties ?? []).map((property) => (
                <li key={property.id} className="work-row map-property">
                  <strong>{property.customerName ?? property.label}</strong>
                  <span>
                    {[property.propertyName, property.address].filter(Boolean).join(' · ') ||
                      words('No address', 'Sin dirección')}
                    {(!Number.isFinite(property.lat) || !Number.isFinite(property.lng)) && (
                      <> · {words('Location missing', 'Falta ubicación')}</>
                    )}
                  </span>
                  {property.customerId && (
                    <Link to={`/customers/${property.customerId}`}>
                      {words('View customer / fix address', 'Ver cliente / corregir dirección')}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            {data.data?.properties?.length === 0 && (
              <WorkEmpty
                title={words(
                  'Add a customer address to see it here.',
                  'Agregue la dirección de un cliente para verla aquí.',
                )}
              >
                <Link className="btn btn-secondary mt-3" to="/customers">
                  {words('Add customer', 'Agregar cliente')}
                </Link>
              </WorkEmpty>
            )}
          </WorkSection>
        )}
        {!data.error && !customers && data.data?.entitlements?.drag && (
          <div className="card flex flex-wrap gap-3" aria-label="Crew lanes">
            {crews.map((crew) => (
              <button
                key={crew.userId}
                className="btn btn-secondary"
                disabled={busy}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const visit = visits.find(
                    (v) => v.id === event.dataTransfer.getData('application/x-blastoclean-visit'),
                  );
                  if (visit)
                    void mutate(
                      `/schedule/appointments/${visit.id}/assignees`,
                      { expectedVersion: visit.version, assigneeIds: [crew.userId] },
                      true,
                    );
                }}
                onClick={() => {
                  const visit = visits.find((v) => v.id === selected);
                  if (visit)
                    void mutate(
                      `/schedule/appointments/${visit.id}/assignees`,
                      { expectedVersion: visit.version, assigneeIds: [crew.userId] },
                      true,
                    );
                }}
              >
                {crew.name} · {visits.filter((v) => v.assigneeIds.includes(crew.userId)).length}
              </button>
            ))}
          </div>
        )}
        {!data.error && !customers && (
          <WorkSection title={words('Visits', 'Visitas')}>
            {data.data && visits.length === 0 && (
              <WorkEmpty title={words('No visits for this selection', 'No hay visitas para esta selección')}>
                {colorFilter ? (
                  <Button className="mt-3" variant="secondary" onClick={() => setColorFilter('')}>
                    {words('Clear filter', 'Quitar filtro')}
                  </Button>
                ) : (
                  words(
                    'Choose another day or open the schedule to plan a visit.',
                    'Elija otro día o abra la agenda para planificar una visita.',
                  )
                )}
              </WorkEmpty>
            )}
            <ul className="map-rows">
              {visits.map((visit) => (
                <li key={visit.id} className="work-row map-visit" data-selected={selected === visit.id}>
                  <button
                    type="button"
                    className="map-visit-select"
                    aria-pressed={selected === visit.id}
                    onClick={() => setSelected(visit.id)}
                    onKeyDown={(event) => {
                      if (event.key.toLowerCase() === 'r') {
                        event.preventDefault();
                        document.getElementById(`map-crew-${visit.id}`)?.focus();
                      }
                    }}
                  >
                    <strong>
                      {visit.label}
                      <VisitColorSignals colors={visit.colors} />
                    </strong>
                  </button>{' '}
                  · {i18n.t(`schedule.appointmentStatus.${visit.status}`, { defaultValue: visit.status })} ·{' '}
                  {formatInstantTime(visit.startsAt, timezone)}
                  {!visit.property && <span> · {words('Location missing', 'Falta ubicación')}</span>}
                  {data.data?.entitlements?.drag && ['scheduled', 'confirmed'].includes(visit.status) && (
                    <form
                      className="map-assignment"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const chosen = crewChoice[visit.id];
                        if (chosen !== undefined)
                          void mutate(
                            `/schedule/appointments/${visit.id}/assignees`,
                            { expectedVersion: visit.version, assigneeIds: chosen ? [chosen] : [] },
                            true,
                          );
                      }}
                    >
                      <label>
                        {words('Crew', 'Equipo')}{' '}
                        <select
                          className="field-control"
                          id={`map-crew-${visit.id}`}
                          aria-label={`Crew for ${visit.label}`}
                          disabled={busy}
                          value={
                            crewChoice[visit.id] ??
                            (visit.assigneeIds.length === 1
                              ? visit.assigneeIds[0]
                              : visit.assigneeIds.length
                                ? '__multiple'
                                : '')
                          }
                          onChange={(e) => setCrewChoice((previous) => ({ ...previous, [visit.id]: e.target.value }))}
                        >
                          {visit.assigneeIds.length > 1 && (
                            <option value="__multiple" disabled>
                              {words('Multiple people', 'Varias personas')}
                            </option>
                          )}
                          <option value="">{words('Unassigned', 'Sin asignar')}</option>
                          {crews.map((c) => (
                            <option key={c.userId} value={c.userId}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        className="btn btn-primary"
                        type="submit"
                        disabled={busy || crewChoice[visit.id] === undefined}
                      >
                        {words('Confirm', 'Confirmar')}
                      </button>
                    </form>
                  )}
                </li>
              ))}
            </ul>
          </WorkSection>
        )}
        {!data.error &&
          data.data?.entitlements?.drag &&
          data.data.openSlots.map((slot) => (
            <div key={slot.id} className="card map-opening">
              <span>
                {words('Open slot', 'Horario disponible')} {formatInstantDateTime(slot.startsAt, timezone)}
              </span>
              <select
                className="field-control"
                aria-label="Fill opening"
                disabled={busy}
                value=""
                onChange={(e) => {
                  const visit = visits.find((v) => v.id === e.target.value);
                  if (visit)
                    void mutate(`/schedule/slot-openings/${slot.id}/claim`, {
                      expectedVersion: slot.version,
                      appointmentId: visit.id,
                      appointmentExpectedVersion: visit.version,
                    });
                }}
              >
                <option value="">{words('Choose a visit', 'Elegir visita')}</option>
                {visits
                  .filter((v) => v.id !== slot.appointmentId && ['scheduled', 'confirmed'].includes(v.status))
                  .map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
              </select>
              <button
                className="btn btn-secondary"
                disabled={busy}
                onClick={() =>
                  void mutate(`/schedule/slot-openings/${slot.id}/dismiss`, { expectedVersion: slot.version })
                }
              >
                {words('Dismiss', 'Descartar')}
              </button>
            </div>
          ))}
      </div>
    </WorkPage>
  );
}
